import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import {
  GridToolbar,
} from '@mui/x-data-grid-premium';
// import { paymentFilterOperator } from './DataGridComponnent/PaymentFilterOperator';


import { Modal, Button, Typography, TablePagination } from '@mui/material';
import DataGridActions from './DataGridActions';

function DataGridServerSide({ columns, rows, handleEdit, handleDelete, handleShowPrint, showPayment, height, rowCount, loading, paginationModel,
  sortModel, filterModel, setPaginationModel, setSortModel, setFilterModel, setQuickFilter, showDetailsStatus,
  actionsMinWidth, actionsFlex,
  summaryFooter }) {
  const summaryRowBackground = 'var(--dg-summary-bg)';
  const gridContainerRef = useRef(null);
  const summaryContentRef = useRef(null);
  const pendingScrollFrameRef = useRef(null);
  const lastScrollLeftRef = useRef(0);
  const [summaryColumnWidths, setSummaryColumnWidths] = useState([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);

  const handleDeleteClick = (id) => {
    setDeleteItemId(id); // Vendos id e rreshtit që duhet të fshihet
    setShowDeleteConfirmation(true); // Hap modalin për konfirmim
  };

  const gridColumns = [];

  if (showPayment) {
    gridColumns.push({
      field: 'PaymentOptions',
      headerName: 'Pagesa',
      minWidth: 90,
      flex: 0.8,
      renderCell: (params) => (
        <>
          {
            params.row.rowInfo.PaymentOptions === 0 ? (<Chip label="Pa Paguar" color="error" />) : (<Chip label="Paguar" color="success" />)
          }
        </>
      ),
      headerClassName: 'actions-header',
      // filterOperators: [paymentFilterOperator], // kjo është linja e re
    });
  }


  gridColumns.push({
    field: 'actions',
    headerName: 'Veprimet',
    renderCell: (params) => (
      <DataGridActions
        back
        rowData={params.row.rowInfo}
        handleEdit={handleEdit}
        handleDelete={() => handleDeleteClick(params.row.deleteItemId)}
        showDetails={handleShowPrint}
        showDetailsStatus={showDetailsStatus}
      />
    ),
    headerClassName: 'actions-header',
    minWidth: actionsMinWidth ?? (showDetailsStatus ? 110 : 90),
    flex: actionsFlex ?? (showDetailsStatus ? 1.05 : 0.85),
  });

  const allColumns = [...columns, ...gridColumns];
  const hasSummaryFooter = Boolean(summaryFooter);

  const summaryColumns = (() => {
    if (!summaryFooter) {
      return allColumns;
    }

    const lastUsefulIndex = allColumns.reduce((accumulator, column, index) => {
      if (column.field === summaryFooter.labelField) {
        return Math.max(accumulator, index);
      }

      const rawValue = summaryFooter.values?.[column.field];
      const hasValue = rawValue !== undefined && rawValue !== null && rawValue !== '';
      return hasValue ? index : accumulator;
    }, -1);

    return lastUsefulIndex >= 0 ? allColumns.slice(0, lastUsefulIndex + 1) : allColumns;
  })();

  const fallbackSummaryGridTemplateColumns = summaryColumns
    .map((column) => `${column.width || column.minWidth || 140}px`)
    .join(' ');

  const summaryGridTemplateColumns = useMemo(() => {
    if (summaryColumnWidths.length !== summaryColumns.length) {
      return fallbackSummaryGridTemplateColumns;
    }

    return summaryColumnWidths.map((width) => `${width}px`).join(' ');
  }, [summaryColumnWidths, summaryColumns.length, fallbackSummaryGridTemplateColumns]);

  const measuredSummaryWidth = summaryColumnWidths.reduce((sum, width) => sum + width, 0);

  useEffect(() => {
    if (!summaryFooter) {
      return undefined;
    }

    const container = gridContainerRef.current;
    if (!container) {
      return undefined;
    }

    const virtualScroller = container.querySelector('.MuiDataGrid-virtualScroller');
    const horizontalScrollbar = container.querySelector('.MuiDataGrid-scrollbar--horizontal');
    const horizontalScrollbarContent = horizontalScrollbar?.querySelector('div');

    if (!virtualScroller && !horizontalScrollbarContent) {
      return undefined;
    }

    const syncSummaryPosition = (scrollLeft) => {
      if (!summaryContentRef.current) {
        return;
      }

      if (Math.abs(scrollLeft - lastScrollLeftRef.current) < 0.25) {
        return;
      }

      lastScrollLeftRef.current = scrollLeft;
      summaryContentRef.current.style.transform = `translate3d(-${scrollLeft}px, 0, 0)`;
    };

    const queueScrollSync = () => {
      if (pendingScrollFrameRef.current !== null) {
        return;
      }

      pendingScrollFrameRef.current = requestAnimationFrame(() => {
        syncSummaryPosition(lastScrollLeftRef.current);
        pendingScrollFrameRef.current = null;
      });
    };

    const onVirtualScroll = (event) => {
      lastScrollLeftRef.current = event.currentTarget.scrollLeft || 0;
      queueScrollSync();
    };

    const onScrollbarScroll = (event) => {
      lastScrollLeftRef.current = event.currentTarget.scrollLeft || 0;
      queueScrollSync();
    };

    lastScrollLeftRef.current =
      (virtualScroller?.scrollLeft || 0) ||
      (horizontalScrollbarContent?.scrollLeft || 0);
    syncSummaryPosition(lastScrollLeftRef.current);

    virtualScroller?.addEventListener('scroll', onVirtualScroll, { passive: true });
    horizontalScrollbarContent?.addEventListener('scroll', onScrollbarScroll, { passive: true });

    return () => {
      virtualScroller?.removeEventListener('scroll', onVirtualScroll);
      horizontalScrollbarContent?.removeEventListener('scroll', onScrollbarScroll);

      if (pendingScrollFrameRef.current !== null) {
        cancelAnimationFrame(pendingScrollFrameRef.current);
        pendingScrollFrameRef.current = null;
      }
    };
  }, [summaryFooter, columns, showDetailsStatus, showPayment]);

  useEffect(() => {
    if (!summaryFooter) {
      setSummaryColumnWidths([]);
      return undefined;
    }

    const container = gridContainerRef.current;
    if (!container) {
      return undefined;
    }

    const measureColumnWidths = () => {
      const widths = summaryColumns.map((column) => {
        const headerCell = container.querySelector(`.MuiDataGrid-columnHeader[data-field="${column.field}"]`);
        if (!headerCell) {
          return column.width || column.minWidth || 140;
        }

        const rect = headerCell.getBoundingClientRect();
        return rect.width || column.width || column.minWidth || 140;
      });

      setSummaryColumnWidths(widths);
    };

    measureColumnWidths();

    const resizeObserver = new ResizeObserver(() => {
      measureColumnWidths();
    });

    const root = container.querySelector('.MuiDataGrid-root');
    if (root) {
      resizeObserver.observe(root);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [summaryFooter, summaryColumns, columns, showDetailsStatus, showPayment, rows.length, rowCount]);


  return (
    <Box
      ref={gridContainerRef}
      sx={{
        height: height,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--dg-border)',
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: 'var(--dg-shell-bg)',
        boxShadow: '0 12px 26px rgba(15, 23, 42, 0.08)',
        '& .super-app-theme--header': {
          background: 'var(--dg-header-gradient)',
          color: 'var(--dg-header-text)',
          fontWeight: 700,
          letterSpacing: '0.2px',
        },
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          columns={[
            ...columns,
            ...gridColumns
          ]}
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          pageSizeOptions={[5, 10, 20, 100]}
          paginationModel={paginationModel}
          sortModel={sortModel}
          filterModel={filterModel}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          onPaginationModelChange={setPaginationModel}
          onSortModelChange={setSortModel}
          // onFilterModelChange={setFilterModel}
          onFilterModelChange={(newModel) => {
            setFilterModel(newModel);
            if (newModel.quickFilterValues) {
              setQuickFilter(newModel.quickFilterValues);
            }
          }}
          getRowClassName={(params) => {
            const baseClass = params.row.className || '';
            const stripeClass =
              params.indexRelativeToCurrentPage % 2 === 0
                ? 'dg-row-even'
                : 'dg-row-odd';
            return `${baseClass} ${stripeClass}`.trim();
          }}
          initialState={{
            density: 'compact'
          }}
          disableRowSelectionOnClick
          hideFooter={hasSummaryFooter}
          sx={{
            border: 0,
            backgroundColor: 'var(--dg-surface)',
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: '1px solid var(--dg-border)',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.83rem',
              color: 'var(--dg-header-text)',
            },
            '& .MuiDataGrid-columnSeparator': {
              opacity: 0.15,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid var(--dg-cell-border)',
              color: 'var(--dg-cell-text)',
              fontSize: '0.86rem',
            },
            '& .MuiDataGrid-row.dg-row-even': {
              backgroundColor: 'var(--dg-row-even)',
            },
            '& .MuiDataGrid-row.dg-row-odd': {
              backgroundColor: 'var(--dg-row-odd)',
              boxShadow: 'inset 0 1px 0 rgba(40, 73, 69, 0.07), inset 0 -1px 0 rgba(40, 73, 69, 0.07)',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'var(--dg-row-hover)',
            },
            '& .MuiDataGrid-row.dg-row-odd:hover': {
              backgroundColor: 'var(--dg-row-odd-hover)',
            },
            '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: 'rgba(40, 73, 69, 0.14)',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '10px 12px',
              borderBottom: '1px solid var(--dg-border)',
              background: 'var(--dg-toolbar-bg)',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
              color: 'var(--dg-toolbar-text)',
              textTransform: 'none',
              fontWeight: 600,
            },
            '& .MuiDataGrid-toolbarContainer .MuiInputBase-root': {
              borderRadius: '10px',
              backgroundColor: 'var(--dg-input-bg)',
              color: 'var(--dg-input-text)',
              border: '1px solid var(--dg-input-border)',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid var(--dg-border)',
              background: 'var(--dg-footer-bg)',
              minHeight: 54,
            },
            '& .MuiDataGrid-footerContainer .MuiTablePagination-root': {
              width: '100%',
              color: 'var(--dg-pagination-text)',
              fontSize: '0.82rem',
            },
            '& .MuiDataGrid-footerContainer .MuiTablePagination-toolbar': {
              minHeight: 54,
              paddingLeft: '12px',
              paddingRight: '12px',
              gap: '8px',
            },
            '& .MuiDataGrid-footerContainer .MuiTablePagination-selectLabel, & .MuiDataGrid-footerContainer .MuiTablePagination-displayedRows': {
              color: 'var(--dg-pagination-text)',
              fontWeight: 600,
              margin: 0,
            },
            '& .MuiDataGrid-footerContainer .MuiTablePagination-select': {
              border: '1px solid var(--dg-input-border)',
              borderRadius: '8px',
              padding: '4px 26px 4px 10px',
              backgroundColor: 'var(--dg-input-bg)',
              fontWeight: 600,
              color: 'var(--dg-input-text)',
            },
            '& .MuiDataGrid-footerContainer .MuiTablePagination-actions': {
              marginLeft: '6px',
            },
            '& .MuiDataGrid-footerContainer .MuiTablePagination-actions button': {
              border: '1px solid var(--dg-input-border)',
              borderRadius: '9px',
              padding: '6px',
              marginLeft: '4px',
              color: 'var(--dg-toolbar-text)',
              backgroundColor: 'var(--dg-input-bg)',
              transition: 'all 0.2s ease',
            },
            '& .MuiDataGrid-footerContainer .MuiTablePagination-actions button:hover': {
              borderColor: 'var(--dg-border)',
              backgroundColor: 'var(--dg-row-hover)',
            },
            '& .MuiDataGrid-footerContainer .MuiTablePagination-actions button.Mui-disabled': {
              opacity: 0.45,
              borderColor: '#d8e0e8',
              color: '#94a3b8',
            },
          }}
          slots={{
            toolbar: GridToolbar, // Përdorimi i CustomToolbar në vend të GridToolbar
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true
            },
          }}
        />
      </Box>

      {summaryFooter && (
        <Box sx={{ borderTop: '1px solid #cddbe3', backgroundColor: summaryRowBackground }}>
          <Box
            sx={{
              overflow: 'hidden',
              backgroundColor: summaryRowBackground,
              borderBottom: '1px solid var(--dg-border)',
              boxShadow: 'none',
            }}
          >
            <Box
              ref={summaryContentRef}
              sx={{
                display: 'grid',
                gridTemplateColumns: summaryGridTemplateColumns,
                width: measuredSummaryWidth > 0 ? `${measuredSummaryWidth}px` : '100%',
                minWidth: 'max-content',
                alignItems: 'center',
                willChange: 'transform',
              }}
            >
              {(() => {
                const labelIndex = summaryColumns.findIndex(
                  (column) => column.field === summaryFooter.labelField,
                );

                if (labelIndex < 0) {
                  return null;
                }

                const mergedSpan = labelIndex + 1;
                const valueColumns = summaryColumns.slice(labelIndex + 1);

                return (
                  <>
                    <Box
                      key="summary-label-merged"
                      sx={{
                        gridColumn: `span ${mergedSpan}`,
                        px: 1.75,
                        py: 1.25,
                        borderRight: '1px solid var(--dg-border)',
                        borderBottom: '1px solid rgba(40, 73, 69, 0.06)',
                        color: 'var(--dg-summary-text)',
                        fontWeight: 800,
                        fontSize: '0.87rem',
                        letterSpacing: '0.15px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'center',
                        backgroundColor: summaryRowBackground,
                      }}
                    >
                      {`Σ ${summaryFooter.label}`}
                    </Box>

                    {valueColumns.map((column) => {
                      const cellValue = summaryFooter.values?.[column.field] ?? '';
                      const hasValueCell = cellValue !== '';

                      return (
                        <Box
                          key={`summary-${column.field}`}
                          sx={{
                            px: 1.75,
                            py: 1.25,
                            borderRight: 'none',
                            borderBottom: '1px solid rgba(40, 73, 69, 0.06)',
                            color: 'var(--dg-summary-text)',
                            fontWeight: hasValueCell ? 700 : 500,
                            fontSize: '0.83rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textAlign: 'left',
                            fontVariantNumeric: hasValueCell ? 'tabular-nums' : 'normal',
                            backgroundColor: summaryRowBackground,
                          }}
                        >
                          {cellValue}
                        </Box>
                      );
                    })}
                  </>
                );
              })()}
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: 'var(--dg-footer-bg)',
            }}
          >
            <TablePagination
              component="div"
              count={rowCount || 0}
              page={paginationModel?.page || 0}
              onPageChange={(_, newPage) =>
                setPaginationModel?.((prev) => ({
                  ...(prev || { pageSize: paginationModel?.pageSize || 10 }),
                  page: newPage,
                }))
              }
              rowsPerPage={paginationModel?.pageSize || 10}
              onRowsPerPageChange={(event) => {
                const nextPageSize = parseInt(event.target.value, 10);
                setPaginationModel?.({ page: 0, pageSize: nextPageSize });
              }}
              rowsPerPageOptions={[5, 10, 20, 100]}
              sx={{
                minHeight: 54,
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
                '& .MuiTablePagination-toolbar': {
                  minHeight: 54,
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  gap: '8px',
                  justifyContent: 'flex-end',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  color: 'var(--dg-pagination-text)',
                  fontWeight: 600,
                  margin: 0,
                  textAlign: 'left',
                },
                '& .MuiTablePagination-selectLabel': {
                  marginLeft: 'auto',
                },
                '& .MuiTablePagination-displayedRows': {
                  marginLeft: 0,
                },
                '& .MuiTablePagination-select': {
                  border: '1px solid var(--dg-input-border)',
                  borderRadius: '8px',
                  padding: '4px 26px 4px 10px',
                  backgroundColor: 'var(--dg-input-bg)',
                  fontWeight: 600,
                  color: 'var(--dg-input-text)',
                },
                '& .MuiTablePagination-actions button': {
                  border: '1px solid var(--dg-input-border)',
                  borderRadius: '9px',
                  padding: '6px',
                  marginLeft: '4px',
                  color: 'var(--dg-toolbar-text)',
                  backgroundColor: 'var(--dg-input-bg)',
                },
                '& .MuiTablePagination-actions button:hover': {
                  borderColor: 'var(--dg-border)',
                  backgroundColor: 'var(--dg-row-hover)',
                },
              }}
            />
          </Box>
        </Box>
      )}

      <style jsx>{`
  .actions-header {
    background-color: #284945; /* Ngjyra e sfondit për titullin e kolonës "Actions" */
    color: #FFFFFF; /* Ngjyra e shkronjave e bardhë */  
  }
  .actions-cell {
    background-color: #284945; /* Ngjyra e sfondit për qelizën e kolonës "Actions" */
  }
  .zero-balance-row {
    background-color: lightgray;
  }
  .non-zero-balance-row {
    /* Stili për rreshtat kur 'balance' nuk është 0 */
  }
`}</style>

      {/* Modal për konfirmim të fshirjes */}
      <Modal
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ width: 400, bgcolor: 'background.paper', borderRadius: '4px', boxShadow: 24, p: 4 }}>
          <Typography id="delete-modal-title" variant="h5">Konfirmim</Typography>
          <Typography id="delete-modal-description" variant="body1">A jeni të sigurt se dëshironi ta fshini këtë rresht</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button variant="contained" color="secondary" onClick={() => { handleDelete(deleteItemId); setShowDeleteConfirmation(false); }}>Po</Button>
            <Button variant="contained" onClick={() => setShowDeleteConfirmation(false)}>Jo</Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}


export default DataGridServerSide;
